import stats from "./automation-hub-stats.json" with { type: "json" };
import guides from "./automation-hub-guides.json" with { type: "json" };
import ideasData from "./automation-ideas.json" with { type: "json" };
import toolPages from "./automation-tool-pages.json" with { type: "json" };
import { automationIdeas, type AutomationIdea, workflowsUpdated } from "./automationIdeas.ts";
import { publicPage } from "./publicPages.ts";

type HubCopy = {
  /** The filter value in the ideas data, such as "Sales" or "Real estate". */
  readonly value: string;
  /** Page heading. */
  readonly heading: string;
  /**
   * The phrase the page targets, in sentence case: the visible heading, and with Title Case and
   * the workflow count, the search title. Picked by US search volume and keyword difficulty
   * (DataForSEO, October 2026).
   */
  readonly searchPhrase: string;
  readonly intro: string;
  /** Three workflows to try first, with one line on why. */
  readonly start: readonly { readonly id: string; readonly why: string }[];
  /** Questions specific to this team or industry; shared ones are added on the page. */
  readonly faq: readonly { readonly question: string; readonly answer: string }[];
  /** The matching use case page, when there is one. */
  readonly useCase?: UseCasePath;
};

type UseCasePath =
  | "/use-cases/engineering"
  | "/use-cases/qa"
  | "/use-cases/support"
  | "/use-cases/product"
  | "/use-cases/agencies";

const teamCopy: readonly HubCopy[] = [
  {
    value: "Customer support",
    heading: "AI workflows for customer support",
    searchPhrase: "AI agents for customer service",
    intro:
      "Draft replies from past answers, spot issues before they spread, reproduce bugs and keep help articles current. Next to your help desk, not instead of it.",
    start: [
      {
        id: "SUP01",
        why: "Saves the most agent time on the questions you already answer every day.",
      },
      { id: "SUP02", why: "Catches a new problem after 10 tickets instead of 100." },
      { id: "SUP06", why: "Keeps the help center true after every release." },
    ],
    faq: [
      {
        question: "Does it replace our help desk?",
        answer:
          "No. It works next to Intercom, Pylon, Zoho Desk, Freshdesk and others. Your agents keep the queue and send the replies.",
      },
      {
        question: "Can it look at the code to answer?",
        answer:
          "Yes, if you connect your repository. It answers how the product really works and can reproduce bugs.",
      },
    ],
    useCase: "/use-cases/support",
  },
  {
    value: "Customer success",
    heading: "AI workflows for customer success",
    searchPhrase: "AI for customer success",
    intro:
      "Churn warnings, QBR prep, renewal lists and account plans, built from your usage data, tickets and CRM instead of by hand.",
    start: [
      {
        id: "CS01",
        why: "Gives you a list of at-risk accounts every week, with the reason for each.",
      },
      { id: "CS02", why: "Turns hours of QBR prep into a draft you review." },
      { id: "CS09", why: "No renewal sneaks up on the team." },
    ],
    faq: [
      {
        question: "Which data does it use?",
        answer:
          "Whatever you connect: product analytics for usage, your help desk for tickets, your CRM and billing for contracts and renewals.",
      },
      {
        question: "Does it contact customers?",
        answer: "It drafts check-ins and updates. A CSM decides what to send.",
      },
    ],
  },
  {
    value: "Engineering",
    heading: "AI workflows for engineering teams",
    searchPhrase: "AI for developers",
    intro:
      "Tickets to tested pull requests, first-pass reviews, changelogs, postmortems and dependency upgrades. Work your team keeps putting off.",
    start: [
      { id: "ENG01", why: "Small tickets stop waiting for someone to have time." },
      { id: "ENG02", why: "A first review on every pull request before a person looks." },
      { id: "ENG04", why: "A readable changelog without the Friday rush." },
    ],
    faq: [
      {
        question: "Does it merge code?",
        answer: "No. It opens a pull request with tests. Your team reviews and merges.",
      },
      {
        question: "Where does it run the code?",
        answer:
          "Each coding task gets its own sandbox, branch and checkout, so several can run at once.",
      },
    ],
    useCase: "/use-cases/engineering",
  },
  {
    value: "QA",
    heading: "AI workflows for QA",
    searchPhrase: "QA automation with AI",
    intro:
      "Test every branch in a real browser, run nightly regressions and reproduce reported bugs, with a video of what happened.",
    start: [
      { id: "QA01", why: "Every change gets clicked through before merge, with a video." },
      { id: "QA02", why: "Regressions show up in the morning report, not from customers." },
      { id: "QA03", why: 'Answers "is this new?" with a recording of both versions.' },
    ],
    faq: [
      {
        question: "Does it test in a real browser?",
        answer:
          "Yes. It starts the app, clicks through the flow in a browser and records a video of what happened.",
      },
      {
        question: "Do we need test scripts?",
        answer:
          "No. Describe the flow in plain words. You can save the steps as a skill to repeat them.",
      },
    ],
    useCase: "/use-cases/qa",
  },
  {
    value: "Product",
    heading: "AI workflows for product managers",
    searchPhrase: "AI for product managers",
    intro:
      "Answers from the code, specs with the edge cases, feedback themes and metric investigations, without waiting for an engineer or an analyst.",
    start: [
      { id: "PRD01", why: "Know what a change touches before it's on the roadmap." },
      { id: "PRD04", why: "See what users ask for most, every week, with counts and quotes." },
      { id: "PRD02", why: "Specs that include the edge cases the code already handles." },
    ],
    faq: [
      {
        question: "Does it need access to our code?",
        answer:
          "For answers about how the product works, yes. Feedback, interviews and research work without it.",
      },
      {
        question: "Can it build a prototype?",
        answer: "Yes. It can build a rough version on a branch and share a preview and a video.",
      },
    ],
    useCase: "/use-cases/product",
  },
  {
    value: "Data",
    heading: "AI workflows for data teams",
    searchPhrase: "AI for data analysis",
    intro:
      "Answer data questions in Slack, check daily loads, fix SQL and send the weekly metrics, so the data team stops being a help desk.",
    start: [
      { id: "DAT01", why: "Takes the simple questions off the data team's queue." },
      { id: "DAT04", why: "Bad data gets caught before it reaches a dashboard." },
      { id: "DAT06", why: "The Monday numbers arrive without anyone screenshotting dashboards." },
    ],
    faq: [
      {
        question: "Which databases can it query?",
        answer:
          "BigQuery and Supabase connect through MCP. Others work through a skill with read-only credentials in the Vault.",
      },
      {
        question: "Can we check its queries?",
        answer: "Yes. Ask for the query behind any answer and it shows the SQL it ran.",
      },
    ],
  },
  {
    value: "IT and admin",
    heading: "AI workflows for IT and admin",
    searchPhrase: "IT automation with AI",
    intro:
      "Help-channel answers, access requests, access reviews and vendor checks, following the procedures you already wrote down.",
    start: [
      {
        id: "IT01",
        why: "Answers the everyday questions in your IT channel from your own guides.",
      },
      { id: "IT02", why: "Every access request gets the right approver and a record." },
      { id: "IT03", why: "Access reviews become a quarterly list, not an audit scramble." },
    ],
    faq: [
      {
        question: "Can it change access in our tools?",
        answer:
          "It can if you connect the tool and allow it. Most teams keep granting access with IT and let it collect approvals.",
      },
      {
        question: "Does it follow our runbooks?",
        answer: "Yes. Share them once and it walks through your documented steps.",
      },
    ],
  },
  {
    value: "Founders and leadership",
    heading: "AI workflows for founders and leaders",
    searchPhrase: "AI for founders and executives",
    intro:
      "Catch-ups, inbox triage, investor updates, board decks and meeting follow-through. Get hours back for the work only you can do.",
    start: [
      { id: "LEAD01", why: "The quickest way to get through a day of missed messages." },
      { id: "LEAD03", why: "Your inbox sorted twice a day, with drafts ready." },
      { id: "LEAD06", why: "Decisions from meetings turn into tasks with owners." },
    ],
    faq: [
      {
        question: "Does it send email for me?",
        answer: "It drafts. You send, unless you tell it otherwise.",
      },
      {
        question: "Can it use our real numbers for investor updates?",
        answer: "Yes, from the billing, accounting and analytics tools you connect.",
      },
    ],
  },
];

const industryCopy: readonly HubCopy[] = [
  {
    value: "Software",
    heading: "AI workflows for software companies",
    searchPhrase: "AI for SaaS companies",
    intro:
      "From support and success to engineering, QA and product. Work that touches your code, your customers and your releases.",
    start: [
      { id: "SUP04", why: "Turns vague customer reports into bugs engineers can fix." },
      { id: "ENG01", why: "Small tickets turn into tested pull requests." },
      { id: "QA02", why: "Your key flows get tested every night." },
    ],
    faq: [
      {
        question: "Does it work with our repositories?",
        answer:
          "Yes. Connect GitHub or GitLab and it reads the code, runs your checks and opens pull requests.",
      },
      {
        question: "Is it only for engineers?",
        answer:
          "No. Support, product and success teams use it to answer questions from the code and the tools they already use.",
      },
    ],
    useCase: "/use-cases/engineering",
  },
];

/** Questions every team and industry page answers, from the site FAQ's facts. */
export const sharedIdeaFaqs = [
  {
    question: "How long does it take to set up?",
    answer:
      "Minutes for the workspace: create it, connect your AI provider and connect Slack. Each workflow then takes one request to your agent, plus signing in to any tool it needs.",
  },
  {
    question: "Do we need a developer?",
    answer:
      "No. You set up each workflow by asking your agent in plain words. A repository is only needed for coding work.",
  },
  {
    question: "What does it cost?",
    answer:
      "$149 per workspace per month, with unlimited people and $20 in cloud credits each month. Your AI provider bills its usage separately.",
  },
] as const;

export type IdeaHubKind = "team" | "industry" | "tool";

const smallWords = new Set(["a", "and", "for", "in", "of", "or", "the", "to", "with"]);

/** "AI for sales teams" → "AI for Sales Teams"; acronyms and small words stay as written. */
function titleCase(phrase: string) {
  return phrase
    .split(" ")
    .map((word, index) =>
      index > 0 && smallWords.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

export type IdeaHub = HubCopy & { readonly kind: IdeaHubKind; readonly slug: string };

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Tool names a workflow uses, as shown on the page: "Jira (Atlassian)" reads as "Jira". */
export const workflowToolNames = (idea: Pick<AutomationIdea, "maito">) => [
  ...new Set(
    [
      ...idea.maito.connect.flatMap((c) => c.servers),
      ...idea.maito.skill.flatMap((c) => c.examples),
    ].map((name) => name.replace(/\s*\(.*\)$/, "")),
  ),
];

/**
 * A page per tool that people search for ("automate jira", "salesforce ai agent"), with copy
 * built from the workflows that use it.
 */
function toolCopy(name: string, phrase: string): HubCopy {
  const ideas = ideasData.filter((idea) => workflowToolNames(idea).includes(name));
  const viaMcp = ideas.some((idea) =>
    idea.maito.connect.some((c) => c.servers.some((s) => s.replace(/\s*\(.*\)$/, "") === name)),
  );
  const teams = [...new Set(ideas.map((idea) => idea.team))];
  return {
    value: name,
    heading: `AI workflows that use ${name}`,
    searchPhrase: phrase,
    intro: viaMcp
      ? `Jobs Maito handles with ${name}, from Slack or email. ${name} connects through its MCP server, so setup is a sign-in.`
      : `Jobs Maito handles with ${name}, from Slack or email. ${name} works through a skill: your steps, with a login or API key kept in the Vault.`,
    start: ideas.slice(0, 3).map((idea) => ({ id: idea.id, why: idea.pitch })),
    faq: [
      {
        question: `How does Maito connect to ${name}?`,
        answer: viaMcp
          ? `Through ${name}'s MCP server. Connect it once in Maito; if it isn't connected yet, your agent sends you a link to sign in.`
          : `Through a skill. Save the steps once and add the login or API key in Settings → Vault. Maito then uses the API or signs in through the browser.`,
      },
      {
        question: `Can it change things in ${name}?`,
        answer: `Only what you allow. You choose which ${name} actions Maito can use, and the workflows here prepare changes for a person to approve unless you set them to run on their own.`,
      },
      {
        question: "Which teams use these workflows?",
        answer: `${teams.slice(0, -1).join(", ")}${teams.length > 1 ? " and " : ""}${teams.at(-1) ?? ""}.`,
      },
    ],
  };
}

const hubIncludes = (hub: IdeaHub, idea: AutomationIdea) =>
  hub.kind === "team"
    ? idea.team === hub.value
    : hub.kind === "tool"
      ? workflowToolNames(idea).includes(hub.value)
      : idea.industries.includes(hub.value);

/** Every hub with copy. */
const allIdeaHubs: readonly IdeaHub[] = [
  ...teamCopy.map((copy) => ({ ...copy, kind: "team" as const, slug: slugify(copy.value) })),
  ...industryCopy.map((copy) => ({
    ...copy,
    kind: "industry" as const,
    slug: slugify(copy.value),
  })),
  ...toolPages.map((tool) => ({
    ...toolCopy(tool.name, tool.phrase),
    kind: "tool" as const,
    slug: slugify(tool.name),
  })),
];

/** Published hubs: those with at least one workflow. */
export const ideaHubs = allIdeaHubs.filter((hub) =>
  ideasData.some((idea) => hubIncludes(hub, idea)),
);

/** The page for a tool, by its display name, when one exists. */
export const toolHub = (name: string) =>
  ideaHubs.find((hub) => hub.kind === "tool" && hub.value === name);

export const ideaHubPath = (hub: IdeaHub) => `/workflows/${hub.kind}/${hub.slug}`;

export function findIdeaHub(kind: IdeaHubKind, slug: string) {
  return ideaHubs.find((hub) => hub.kind === kind && hub.slug === slug);
}

export function ideaHubFromPath(path: string) {
  const match = /^\/workflows\/(team|industry|tool)\/([a-z0-9-]+)$/.exec(path);
  return match ? findIdeaHub(match[1] as IdeaHubKind, match[2]!) : undefined;
}

export const industryHub = (industry: string) =>
  ideaHubs.find((hub) => hub.kind === "industry" && hub.value === industry);

export function teamHub(team: string) {
  return ideaHubs.find((hub) => hub.kind === "team" && hub.value === team);
}

/** The page a workflow belongs under: its industry when it's industry-specific, otherwise its team. */
export function ideaParentHub(idea: AutomationIdea) {
  const industry = ideaHubs.find(
    (hub) =>
      hub.kind === "industry" && hub.value !== "Software" && idea.industries.includes(hub.value),
  );
  return industry ?? teamHub(idea.team);
}

export function hubIdeas(hub: IdeaHub): AutomationIdea[] {
  return automationIdeas.filter((idea) => hubIncludes(hub, idea));
}

/** The three ideas a page suggests trying first. */
export function hubStartIdeas(hub: IdeaHub) {
  return hub.start.map((item) => ({
    idea: automationIdeas.find((idea) => idea.id === item.id)!,
    why: item.why,
  }));
}

export const hubFaqs = (hub: IdeaHub) => [...hub.faq, ...sharedIdeaFaqs];

/** Search identity for a team or industry page: breadcrumbs, the list of ideas, and questions. */
export function ideaHubPage(hub: IdeaHub) {
  const path = ideaHubPath(hub);
  const ideas = hubIdeas(hub);
  const audience =
    hub.kind === "tool"
      ? `teams using ${hub.value}`
      : hub.heading.replace(/^AI workflows for /, "");
  const examples = hubStartIdeas(hub)
    .map(({ idea }) => idea.title.charAt(0).toLowerCase() + idea.title.slice(1))
    .join(", ");
  return {
    kind: "marketing" as const,
    path,
    title: `${titleCase(hub.searchPhrase)}: ${ideas.length} Workflows`,
    description: `${ideas.length} AI workflows for ${audience}, like ${examples}. Each shows the steps, an example and how to set it up.`,
    updated: workflowsUpdated,
    structuredData: (origin: string) => [
      {
        "@type": "ItemList",
        name: hub.heading,
        numberOfItems: ideas.length,
        itemListElement: ideas.map((idea, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: idea.title,
          url: `${origin}/workflows/${idea.slug}`,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: hubFaqs(hub).map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Workflows", item: `${origin}/workflows` },
          { "@type": "ListItem", position: 2, name: hub.value, item: `${origin}${path}` },
        ],
      },
    ],
  };
}

/** Questions the main workflows page answers, before the shared setup and cost ones. */
export const workflowIndexFaqs = [
  {
    question: "What is an AI workflow?",
    answer:
      "A job you hand to an AI agent that it does the same careful way each time: research before a call, the Monday report, chasing missing invoices. In Maito you ask for it in Slack or by email, or set it up as a Loop that runs on its own.",
  },
  {
    question: "What is a Loop?",
    answer:
      "A recurring assignment for your agent. It runs on a schedule, from a webhook, or when you start it, and posts its results to Slack, email or a channel. You can pause or change it any time.",
  },
  {
    question: "Which tools does it work with?",
    answer:
      "Any tool with an MCP server, such as HubSpot, Salesforce, Gmail, Notion, Linear and Stripe. For a tool without one, save a skill with the steps and keep the login or API key in the Vault.",
  },
  ...sharedIdeaFaqs,
];

/** Search identity for the main workflows page: the team and industry pages, and questions. */
export function workflowsIndexPage() {
  return {
    ...publicPage("/workflows"),
    updated: workflowsUpdated,
    structuredData: (origin: string) => [
      {
        "@type": "ItemList",
        name: "AI workflows by team and industry",
        numberOfItems: ideaHubs.length,
        itemListElement: ideaHubs.map((hub, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: hub.heading,
          url: `${origin}${ideaHubPath(hub)}`,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: workflowIndexFaqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  };
}

export type HubStat = {
  readonly value: string;
  readonly description: string;
  readonly source: string;
  readonly href: string;
};

/** One sourced number about the cost of doing this work by hand, when we have a credible one. */
export const hubStat = (hub: IdeaHub) =>
  (stats as Record<string, HubStat | undefined>)[hub.value] ?? null;

export type HubGuide = { readonly heading: string; readonly paragraphs: readonly string[] };

/** A short guide for a team or industry page: what changes, what stays with people, how to start. */
export const hubGuide = (hub: IdeaHub) =>
  (guides as Record<string, HubGuide | undefined>)[hub.value] ?? null;
